import Button from "@/elements/Button";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import {
  DispositivoType,
  MappaturaAllarmiType,
  MascheraDispositivo,
  ModelloDispositivoDto,
  getGetModelliDispositivoQueryKey,
  useCreateModelloDispositivo,
  useGetModelliDispositivo,
  useUpdateModelloDispositivo,
} from "@/api";
import { Controller, UseFormSetError, useForm } from "react-hook-form";
import { withOpeningReset } from "../../utils/withOpeningReset";
import { SmallLoading } from "../Loading";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import { addServerErrorsFactory } from "@/utils/addServerErrorsFactory";
import { AxiosError } from "axios";
import ModelloDispositivoMappaturaBitmaskDialog from "../ModelloDispositivoMappaturaBitmaskDialog";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ModelloDispositivoMappaturaEqualityDialog from "../ModelloDispositivoMappaturaEqualityDialog";
import { getDispositivoTypes } from "@/utils/dispositivoUtils";
import FileField from "../FileField";
import ProprietaMisurateTable from "./ProprietaMisurateTable";

const availableMaschere = Object.keys(MascheraDispositivo);

function ModelloDispositivoDialog({
  open = false,
  onClose,
  modello,
}: {
  open?: boolean;
  onClose?: () => void;
  modello?: ModelloDispositivoDto | null;
}) {
  const { t, i18n } = useTranslation();
  const modelliDispositivo = useGetModelliDispositivo();

  const handleClose = () => {
    onClose?.();
  };

  const defaultProprietaMisurate = useMemo(() => {
    const values =
      modello?.proprietaMisurataTypes?.map((proprieta) => ({
        ...proprieta,
        graficoAsseYAutomatico: proprieta.graficoAsseYAutomatico ?? false,
        registroAlertBindings: proprieta.registroAlertBindings ?? [],
      })) ?? [];
    return [...values].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [modello]);

  const {
    register,
    control,
    handleSubmit,
    setError,
    formState: { errors, isValid },
    watch,
    setValue,
    resetField,
    getValues,
  } = useForm<ModelloDispositivoDto>({
    defaultValues: {
      ...(modello ?? {}),
      proprietaMisurataTypes: defaultProprietaMisurate,
    },
  });

  const { saveAsync, isLoading } = useMutator(modello, setError);

  const [mappaturaType, setMappaturaType] = useState<MappaturaAllarmiType>(
    modello?.mappaturaAllarmi?.type || MappaturaAllarmiType.Bitmask
  );

  const queryClient = useQueryClient();

  const onSubmit = async (data: ModelloDispositivoDto) => {
    try {
      await saveAsync(data);
      queryClient.invalidateQueries({
        queryKey: getGetModelliDispositivoQueryKey(),
      });
      // queryClient.invalidateQueries({ queryKey: getGetClientiQueryKey() });
      onClose?.();
    } catch (err) {}
  };

  const nomeModello = watch("modello");
  const alreadyExists = useMemo(() => {
    if (!nomeModello || !modelliDispositivo.data?.data) {
      return null;
    }

    return !!modelliDispositivo.data.data.find(
      (m) => m.modello.trim() === nomeModello.trim() && m.id !== modello?.id
    );
  }, [nomeModello, modelliDispositivo.data, modello]);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xl">
      <DialogTitle>
        {modello
          ? t("modelloDispositivoDialog.titoloModifica")
          : t("modelloDispositivoDialog.titoloCrea")}
      </DialogTitle>
      <DialogContent>
        <InputLabel htmlFor="user-modello">
          {t("modelloDispositivoDialog.campi.modello")}
        </InputLabel>
        <TextField
          id="modello-nome"
          autoFocus
          {...register("modello", {
            required: t("validazione.obbligatorio"),
            minLength: 1,
          })}
          error={!!errors.modello?.message}
          helperText={errors.modello?.message || null}
          fullWidth
        />
        {alreadyExists && (
          <FormHelperText error>
            {t("modelloDispositivoDialog.errori.giaEsistente")}
          </FormHelperText>
        )}

        <InputLabel htmlFor="dispositivo-type">
          {t("modelloDispositivoDialog.campi.tipo")}
        </InputLabel>

        <Controller
          control={control}
          name="tipoDispositivo"
          rules={{
            required: t("modelloDispositivoDialog.errori.tipoRichiesto"),
          }}
          render={({ field, fieldState: { error } }) => {
            return (
              <>
                <Select
                  {...field}
                  value={field.value ?? ""}
                  size="small"
                  sx={{ pt: 1 }}
                  id="dispositivo-type"
                >
                  {getDispositivoTypes(i18n.language)
                    .filter(
                      (option) => option.value !== DispositivoType.unknown
                    )
                    .map((option) => {
                      return (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      );
                    })}
                </Select>
                {error && (
                  <FormHelperText error>{error?.message}</FormHelperText>
                )}
              </>
            );
          }}
        />

        <InputLabel htmlFor="user-maschera">
          {t("modelloDispositivoDialog.campi.maschera")}
        </InputLabel>
        <Controller
          control={control}
          name="maschera"
          render={({ field, fieldState: { error } }) => {
            return (
              <>
                <Select
                  {...field}
                  value={field.value ?? ""}
                  size="small"
                  sx={{ pt: 1 }}
                  id="maschera"
                >
                  <MenuItem value="">
                    <em>
                      {t("modelloDispositivoDialog.campi.nessunaMaschera")}
                    </em>
                  </MenuItem>
                  {availableMaschere.map((maschera) => {
                    return (
                      <MenuItem key={maschera} value={maschera}>
                        {maschera}
                      </MenuItem>
                    );
                  })}
                </Select>
                {error && (
                  <FormHelperText error>{error?.message}</FormHelperText>
                )}
              </>
            );
          }}
        />

        <InputLabel htmlFor="user-image">
          {t("modelloDispositivoDialog.campi.immagine")}
        </InputLabel>
        <Controller
          name="image"
          control={control}
          rules={{
            required: t("modelloDispositivoDialog.errori.fileRichiesto"),
          }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <>
              <FileField
                value={value}
                onChange={(f) => {
                  onChange(f);
                  if (f?.id) {
                    setValue("imageId", f?.id);
                  } else {
                    resetField("imageId");
                  }
                }}
              />
              {error && <FormHelperText error>{error?.message}</FormHelperText>}
            </>
          )}
        />

        <Box mt={2}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {t("modelloDispositivoDialog.mappatura.titoloSezione")}
          </Typography>
          <Box
            sx={{
              pl: 3,
              ml: 1.5,
              borderLeft: "2px solid",
              borderColor: "divider",
            }}
          >
            <InputLabel>
              {t("modelloDispositivoDialog.campi.tipoMappatura")}
            </InputLabel>
            <Select
              value={mappaturaType}
              onChange={(e) => {
                setMappaturaType(e.target.value as MappaturaAllarmiType);
              }}
            >
              <MenuItem value={MappaturaAllarmiType.Bitmask}>
                {t("modelloDispositivoDialog.mappatura.bitmask")}
              </MenuItem>
              <MenuItem value={MappaturaAllarmiType.Equality}>
                {t("modelloDispositivoDialog.mappatura.equality")}
              </MenuItem>
            </Select>

            {mappaturaType === MappaturaAllarmiType.Bitmask && (
              <Box mt={1}>
                <InputLabel>
                  {t("modelloDispositivoDialog.mappatura.bitmask")}
                </InputLabel>
                <Controller
                  name={"mappaturaAllarmi"}
                  control={control}
                  render={({ field }) => {
                    return (
                      <ModelloDispositivoMappaturaBitmaskDialog
                        bitmask={field.value}
                        onChange={(event, bitmask) => {
                          field.onChange(bitmask);
                        }}
                      />
                    );
                  }}
                />
              </Box>
            )}

            {mappaturaType === MappaturaAllarmiType.Equality && (
              <Box mt={1}>
                <InputLabel>
                  {t("modelloDispositivoDialog.mappatura.valori")}
                </InputLabel>
                <Controller
                  name={"mappaturaAllarmi"}
                  control={control}
                  render={({ field }) => {
                    return (
                      <ModelloDispositivoMappaturaEqualityDialog
                        equality={field.value}
                        onChange={(event, bitmask) => {
                          field.onChange(bitmask);
                        }}
                      />
                    );
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>

        {/* Proprietà misurate */}
        <Box mt={2}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {t("modelloDispositivoDialog.proprietaMisurate.titolo")}
          </Typography>
          <ProprietaMisurateTable
            control={control}
            register={register}
            errors={errors}
            setValue={setValue}
            getValues={getValues}
            disabled={isLoading}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        {isLoading && <SmallLoading sx={{ mr: 1 }} />}
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={!isValid || isLoading || alreadyExists !== false}
          onClick={handleSubmit(onSubmit)}
        >
          {modello
            ? t("modelloDispositivoDialog.azioni.salva")
            : t("modelloDispositivoDialog.azioni.crea")}
        </Button>
        <Button
          variant="contained"
          color="light"
          disabled={isLoading}
          onClick={handleClose}
        >
          {t("azioni.annulla")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default withOpeningReset(ModelloDispositivoDialog);

function useMutator(
  modello: ModelloDispositivoDto | null | undefined,
  setError: UseFormSetError<ModelloDispositivoDto>
): {
  isLoading: boolean;

  error: AxiosError<unknown, any> | null;
  saveAsync: (data: ModelloDispositivoDto) => Promise<any>;
} {
  const { t } = useTranslation();

  const isNew = !modello?.id;

  const create = useCreateModelloDispositivo({
    mutation: {
      onSuccess: () => {
        toast.success(t("modelloDispositivoDialog.toast.creato"));
      },
      onError: addServerErrorsFactory(setError, () => {
        toast.error(t("errori.richiesta"));
      }),
    },
  });

  const update = useUpdateModelloDispositivo({
    mutation: {
      onSuccess: () => {
        toast.success(t("modelloDispositivoDialog.toast.salvato"));
      },
      onError: addServerErrorsFactory(setError, () => {
        toast.error(t("errori.richiesta"));
      }),
    },
  });

  const { isLoading, error } = isNew ? create : update;

  return {
    isLoading,
    error,
    saveAsync: async (data: ModelloDispositivoDto) => {
      return isNew
        ? create.mutateAsync({ data: data as ModelloDispositivoDto })
        : update.mutateAsync({
            id: modello.id!,
            data: data as ModelloDispositivoDto,
          });
    },
  };
}
